import React, { useEffect } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import Login from "components/Login";
import { RootState } from "stores/Store";
import TierList from "./TierList";
import Champions from "./Champions";
import MetaBreaker from "./MetaBreaker";
import Suggester from "./Suggester";
import "styles/views/Pro.scss";

// Dev backend has no auth: allow the section locally so it can be built and
// demoed without touching Firestore. In production the Firestore `proAccess`
// flag gates the page AND the Cloud Function checks it server-side.
const DEV_OPEN = process.env.NODE_ENV === "development";

const TABS = [
  { path: "tierlist", label: "Tier list" },
  { path: "champions", label: "Champions" },
  { path: "meta-breaker", label: "Meta breaker" },
  { path: "suggester", label: "Suggester" },
];

function ProSection() {
  const user = useSelector((s: RootState) => s.userReducer.user, shallowEqual);

  // Custom tooltip for every [data-tip] element (native title tooltips are
  // slow/unreliable): one fixed div, filled on hover.
  useEffect(() => {
    const tip = document.createElement("div");
    tip.className = "pro-tip";
    document.body.appendChild(tip);
    const over = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest?.("[data-tip]");
      if (!el) return;
      tip.textContent = el.getAttribute("data-tip") || "";
      const r = el.getBoundingClientRect();
      tip.style.left = `${Math.max(
        8,
        Math.min(r.left, window.innerWidth - 320),
      )}px`;
      tip.style.top = `${r.bottom + 6}px`;
      tip.style.display = "block";
    };
    const out = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest?.("[data-tip]"))
        tip.style.display = "none";
    };
    document.addEventListener("mouseover", over);
    document.addEventListener("mouseout", out);
    return () => {
      document.removeEventListener("mouseover", over);
      document.removeEventListener("mouseout", out);
      tip.remove();
    };
  }, []);

  if (!DEV_OPEN && !user) {
    return (
      <div className="pro">
        <div className="pro__gate">
          <Login message="Sign in to access the pro tools." />
        </div>
      </div>
    );
  }

  if (!DEV_OPEN && !user?.proAccess) {
    return (
      <div className="pro">
        <div className="pro__gate">
          <h2>Restricted access</h2>
          <p>
            This section is reserved for authorized accounts. If you believe you
            should have access, please contact us.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pro">
      <header className="pro__topbar">
        <div className="pro__brand">
          DRAFTER<span>&nbsp;PRO</span>
        </div>
        <nav className="pro__nav">
          {TABS.map((t) => (
            <NavLink
              key={t.path}
              to={t.path}
              className={({ isActive }) =>
                `pro__nav-link${isActive ? " pro__nav-link--active" : ""}`
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <Routes>
        <Route path="tierlist" element={<TierList />} />
        <Route path="champions" element={<Champions />} />
        <Route
          path="interactions"
          element={<Navigate to="/pro/champions" replace />}
        />
        <Route path="meta-breaker" element={<MetaBreaker />} />
        <Route path="suggester" element={<Suggester />} />
        <Route path="*" element={<Navigate to="suggester" replace />} />
      </Routes>
    </div>
  );
}

export default ProSection;
